import { AutoStack } from 'components'
import { FieldProps } from 'formik'
import { isValidMailAddress } from 'helpers'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useUserSuggestions } from 'hooks/useUserSuggestions'
import { useWorkerSuggestions } from 'hooks/useWorkerSuggestions'
import {
  Mail,
  mapUserToUserPersonaMaybeArray,
  mapWorkerToUserPersonaMaybeArray,
  PersonType,
  User,
} from 'model'
import {
  DefaultButton,
  IPeoplePickerProps,
  IPersonaProps,
  ISuggestionItemProps,
  NormalPeoplePicker,
  Persona,
  PersonaInitialsColor,
  PersonaSize,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useReadUserQuery } from './PersonPicker.generated'

export type PersonPickerProps = FieldProps<any> & {
  customSuggestions?: User[]
} & Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems'
  >

export type SinglePersonPickerProps = FieldProps<any> & {
  customSuggestions?: User[]
} & Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems' | 'itemLimit'
  >

export type MultiPersonPickerProps = FieldProps<any> & {
  customSuggestions?: User[]
} & Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems'
  >

export type MailAddressPickerProps = FieldProps<Mail> &
  Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems' | 'onChange'
  >

export type MailingListPickerProps = FieldProps<any> &
  Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems'
  >

export const renderSuggestionsItem = (
  props: IPersonaProps,
  _: ISuggestionItemProps<IPersonaProps>
) => {
  const { text, imageUrl, secondaryText, tertiaryText } = props
  return (
    <Persona
      text={text}
      initialsColor={PersonaInitialsColor.lightRed}
      secondaryText={secondaryText}
      tertiaryText={tertiaryText}
      imageUrl={imageUrl}
      size={PersonaSize.large}
    />
  )
}

const FormikUserPickerBase: React.FC<PersonPickerProps> = ({
  field,
  form,
  onChange,
  itemLimit,
  customSuggestions,
  ...pickerProps
}) => {
  const defaultSelectedItems = mapUserToUserPersonaMaybeArray(field.value)
  const { suggestUsers } = useUserSuggestions(
    undefined,
    false,
    customSuggestions
  )

  return (
    <NormalPeoplePicker
      {...pickerProps}
      resolveDelay={300}
      onChange={onChange}
      onRenderSuggestionsItem={renderSuggestionsItem}
      itemLimit={itemLimit}
      defaultSelectedItems={defaultSelectedItems}
      onResolveSuggestions={suggestUsers as any}
      onEmptyInputFocus={
        customSuggestions ? (items) => suggestUsers('', items) : undefined
      }
    />
  )
}

const FormikWorkerPickerBase: React.FC<PersonPickerProps> = ({
  field,
  form,
  onChange,
  itemLimit,
  customSuggestions,
  ...pickerProps
}) => {
  const defaultSelectedItems = mapWorkerToUserPersonaMaybeArray(field.value)
  const { suggestWorkers } = useWorkerSuggestions()

  return (
    <NormalPeoplePicker
      {...pickerProps}
      onChange={onChange}
      itemLimit={itemLimit}
      defaultSelectedItems={defaultSelectedItems}
      onResolveSuggestions={suggestWorkers as any}
    />
  )
}

export const FormikSingleUserPicker: React.FC<SinglePersonPickerProps> = ({
  field,
  form,
  onChange,
  customSuggestions,
  ...pickerPrpos
}) => {
  return (
    <FormikUserPickerBase
      {...pickerPrpos}
      field={field}
      form={form}
      itemLimit={1}
      customSuggestions={customSuggestions}
      onChange={(items) => {
        if (items && items[0]) {
          form.setFieldValue(field.name, (items[0] as any).userId)
        } else {
          form.setFieldValue(field.name, undefined)
        }
        if (onChange) {
          onChange(items)
        }
      }}
    />
  )
}

export const FormikMultiUserPicker: React.FC<MultiPersonPickerProps> = ({
  field,
  form,
  onChange,
  customSuggestions,
  ...pickerPrpos
}) => {
  return (
    <FormikUserPickerBase
      {...pickerPrpos}
      field={field}
      form={form}
      customSuggestions={customSuggestions}
      onChange={(items) => {
        if (items) {
          form.setFieldValue(
            field.name,
            items.map((i: any) => i.userId)
          )
        }
        if (onChange) {
          onChange(items)
        }
      }}
    />
  )
}
export const FormikMultiUserObjectPicker: React.FC<MultiPersonPickerProps> = ({
  field,
  form,
  onChange,
  customSuggestions,
  ...pickerPrpos
}) => {
  const { refetch } = useReadUserQuery({
    variables: { id: 'no-ops value' },
  })

  return (
    <FormikUserPickerBase
      {...pickerPrpos}
      field={field}
      form={form}
      customSuggestions={customSuggestions}
      onChange={(items) => {
        if (items && items.length > 0) {
          // Filter out invalid items (text input without selection)
          const validItems = items.filter((i: any) => i.userId)
          
          if (validItems.length > 0) {
            const fetchPromises = validItems.map((i: any) => {
              const promise = refetch({ id: i.userId })
              return promise
            })
            Promise.all(fetchPromises).then((values) => {
              const users = values.map((element) => element.data.user)
              form.setFieldValue(field.name, users)
            })
          } else {
            // No valid items selected, set to empty array
            form.setFieldValue(field.name, [])
          }
        } else {
          // No items or empty array, clear the field
          form.setFieldValue(field.name, [])
        }

        if (onChange) {
          onChange(items)
        }
      }}
      //   onChange={items => {
      //     if (items) {
      //       const fetchPromises=items.map((i: any) => {

      //           return i.userId
      //         }
      //       )
      //       Promise.all(fetchPromises).then((values)=> {
      //         dbg(values,'VALUES')
      //         form.setFieldValue(field.name,values)
      //       });
      //     }

      //     if (onChange) {
      //       onChange(items)
      //     }
      //   }
      // }
    />
  )
}

export const FormikSingleWorkerPicker: React.FC<SinglePersonPickerProps> = ({
  field,
  form,
  onChange,
  customSuggestions,
  ...pickerPrpos
}) => {
  return (
    <FormikWorkerPickerBase
      {...pickerPrpos}
      field={field}
      form={form}
      itemLimit={1}
      customSuggestions={customSuggestions}
      onChange={(items) => {
        if (items && items[0]) {
          form.setFieldValue(field.name, (items[0] as any).userId)
        } else {
          form.setFieldValue(field.name, undefined)
        }
        if (onChange) {
          onChange(items)
        }
      }}
    />
  )
}

export const FormikMultiWorkerPicker: React.FC<MultiPersonPickerProps> = ({
  field,
  form,
  onChange,
  customSuggestions,
  ...pickerPrpos
}) => {
  return (
    <FormikWorkerPickerBase
      {...pickerPrpos}
      field={field}
      form={form}
      customSuggestions={customSuggestions}
      onChange={(items) => {
        if (items && items[0]) {
          form.setFieldValue(
            field.name,
            items.map((i: any) => i.userId)
          )
        }
        if (onChange) {
          onChange(items)
        }
      }}
    />
  )
}

export const FormikMailAddressPicker: React.FC<
  MailAddressPickerProps & { underline?: boolean }
> = ({ field, form, itemLimit, underline = false, ...pickerProps }) => {
  const defaultSelectedItems =
    field.value && Array.isArray(field.value)
      ? field.value
          .filter((x) => isValidMailAddress(x))
          .map((y) => {
            return {
              userId: y,
              text: y,
            } as IPersonaProps
          })
      : []
  const { suggestUsers } = useUserSuggestions((user: User): IPersonaProps => {
    return {
      userId: user.email,
      text: user.name,
      secondaryText: user.email,
      tertiaryText: user.department,
    } as IPersonaProps
  }, true)

  return (
    <NormalPeoplePicker
      {...pickerProps}
      onChange={(items) => {
        if (items && items[0]) {
          form.setFieldValue(
            field.name,
            items.map((i: any) => i.userId)
          )
        }
      }}
      itemLimit={itemLimit}
      defaultSelectedItems={defaultSelectedItems}
      onResolveSuggestions={suggestUsers as any}
      styles={
        underline ? { text: { borderWidth: 0, borderBottomWidth: 1 } } : {}
      }
    />
  )
}

export const FormikMailingListPicker: React.FC<MailingListPickerProps> = ({
  field,
  form,
  onChange,
  ...pickerProps
}) => {
  const [proving] = useCurrentProving()
  const { t } = useTranslation()
  const { suggestUsers } = useUserSuggestions()
  const participants = proving.participants || []
  const autoFill = participants
    .filter((p) => p.person && p.person.type === PersonType.User)
    .map((p) => p.person as User)
  const [selectedItems, setSelectedItems] = React.useState(
    mapUserToUserPersonaMaybeArray(field.value)
  )

  return (
    <AutoStack tokens={{ childrenGap: 8 }}>
      <NormalPeoplePicker
        {...pickerProps}
        onChange={(items) => {
          if (onChange) {
            onChange(items)
          }
          if (items) {
            form.setFieldValue(
              field.name,
              items.map((i: any) => i.userId)
            )
            setSelectedItems(items)
          }
        }}
        selectedItems={selectedItems}
        onResolveSuggestions={suggestUsers as any}
      />
      <DefaultButton
        text={t('Interne Bearbeiter übernehmen')}
        onClick={() => {
          const items =
            mapUserToUserPersonaMaybeArray(autoFill).concat(selectedItems)
          setSelectedItems(items)
          if (onChange) {
            onChange(items)
          }
          form.setFieldValue(
            field.name,
            items.map((i: any) => i.userId)
          )
        }}
      />
    </AutoStack>
  )
}

export const UserPickerAdvanced: React.FC<
  Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems'
  >
> = ({ onChange, itemLimit, ...pickerProps }) => {
  const { suggestUsers } = useUserSuggestions(undefined, false)

  return (
    <NormalPeoplePicker
      {...pickerProps}
      resolveDelay={300}
      onChange={onChange}
      onRenderSuggestionsItem={renderSuggestionsItem}
      itemLimit={itemLimit}
      onResolveSuggestions={suggestUsers as any}
    />
  )
}

export const WorkerPickerAdvanced: React.FC<
  Omit<
    IPeoplePickerProps,
    'onEmptyInputFocus' | 'onResolveSuggestions' | 'selectedItems'
  >
> = ({ onChange, itemLimit, ...pickerProps }) => {
  const { suggestWorkers } = useWorkerSuggestions()

  return (
    <NormalPeoplePicker
      {...pickerProps}
      onChange={onChange}
      itemLimit={itemLimit}
      onResolveSuggestions={suggestWorkers as any}
    />
  )
}
